import { Fragment } from 'react';
import type { ProjectDetail } from '@/types/odoo';
import type { TabName } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';

interface ProjectSidebarProps {
  detail: ProjectDetail;
  activeTab: TabName;
  onTabChange: (tab: TabName) => void;
  isTmProject?: boolean;
  /** Show the familiar "Collectoren" entry (a shortcut into the Dashboard's
   *  collectoren section — the legacy tab was retired). Owner decides visibility
   *  (mirrors the Dashboard tab's own EasyKit/TM guards). */
  showCollectorenLink?: boolean;
  /** Fired when the "Collectoren" shortcut is clicked. */
  onCollectorenShortcut?: () => void;
  /** True when the user is on the Dashboard *via* the Collectoren shortcut — lets
   *  the shortcut light up as active (and the Dashboard entry step aside). */
  collectorenActive?: boolean;
}

interface TabItem {
  name: TabName;
  label: string;
  icon: React.ReactNode;
  count?: number;
}

function GridIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM13 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1V5zM4 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5zM13 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1v-5z" />
    </svg>
  );
}

function WrenchIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ReceiptIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function PackageIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

function CollectorIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="9" width="18" height="7" rx="1.5" strokeWidth={2} />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 9V5M12 9V5M17 9V5" />
    </svg>
  );
}

function InkoopordersIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
    </svg>
  );
}

const TM_ALLOWED_TABS: TabName[] = ['materiaal', 'documenten'];

export function ProjectSidebar({ detail, activeTab, onTabChange, isTmProject, showCollectorenLink, onCollectorenShortcut, collectorenActive }: ProjectSidebarProps) {
  const { saleOrder } = detail;
  const canTab = usePermissionsStore((s) => s.canTab);

  const projectName = detail.projectName;
  const soNumber = saleOrder?.name ?? '';
  const clientName = saleOrder?.partner_id ? saleOrder.partner_id[1] : '';
  const totalM2 = saleOrder?.x_studio_totaal_aantal_m2_offerte ?? 0;
  const systeem = saleOrder?.x_studio_vo_omschrijvring || '-';

  const allTabs: TabItem[] = [
    { name: 'dashboard', label: 'Dashboard', icon: <GridIcon /> },
    { name: 'installateur_taak', label: 'Installateur Taak', icon: <WrenchIcon /> },
    { name: 'offerte', label: 'Offerte', icon: <ReceiptIcon /> },
    { name: 'planning', label: 'Planning', icon: <CalendarIcon /> },
    { name: 'materiaal', label: 'Materiaal', icon: <PackageIcon /> },
    { name: 'documenten', label: 'Documenten', icon: <FileIcon /> },
    { name: 'inkooporders', label: 'Inkooporders', icon: <InkoopordersIcon /> },
  ];

  // Mirror ProjectView.canShowTab so the sidebar links and the horizontal pills
  // never disagree. The dashboard is off EasyKit projects, and the standalone
  // offerte link is retired wherever the dashboard (which now carries the Offerte
  // section) is shown — leaving the offerte link as the fallback on EK projects.
  const isEasykit = saleOrder?.x_studio_selectiecode === 'EK - Easykit';
  const tabs = allTabs.filter((tab) => {
    if (!canTab(tab.name)) return false;
    if (isTmProject && !TM_ALLOWED_TABS.includes(tab.name)) return false;
    if (isEasykit && tab.name === 'dashboard') return false;
    if (tab.name === 'offerte' && canTab('dashboard') && !isEasykit) return false;
    return true;
  });

  return (
    <aside className="bg-gray-50 rounded-xl p-5">
      {/* Project header */}
      <div className="mb-5">
        <h2 className="text-lg font-extrabold text-td-navy leading-tight break-words">{projectName}</h2>
        {soNumber && (
          <p className="text-sm text-td-muted mt-0.5 break-words">{soNumber}</p>
        )}
        {clientName && (
          <p className="text-sm text-td-navy font-semibold mt-1 break-words">{clientName}</p>
        )}
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Totaal m2</p>
          <p className="text-sm font-bold text-td-navy break-words">{totalM2} m2</p>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Systeem</p>
          <p className="text-sm font-bold text-td-navy break-words">{systeem}</p>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Projectleider</p>
          <p className="text-sm font-bold text-td-navy break-words">{detail.projectleiderName || '-'}</p>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Adres</p>
          <p className="text-sm font-bold text-td-navy break-words">{detail.werfLocation || '-'}</p>
        </div>
      </div>

      {/* Divider */}
      <hr className="border-gray-200 mb-4" />

      {/* Tab navigation */}
      <nav aria-label="Project secties">
        <ul className="space-y-1">
          {tabs.map((tab) => {
            // Dashboard steps aside as "active" while the user is on it via the
            // Collectoren shortcut, so the two entries read as a tab pair.
            const isActive =
              tab.name === 'dashboard'
                ? activeTab === 'dashboard' && !collectorenActive
                : activeTab === tab.name;
            return (
              <Fragment key={tab.name}>
                <li>
                  <button
                    type="button"
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => onTabChange(tab.name)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      isActive
                        ? 'bg-td-blue text-white'
                        : 'text-td-navy hover:bg-gray-100'
                    }`}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                    {tab.count !== undefined && (
                      <span
                        className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full ${
                          isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-td-muted'
                        }`}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                </li>
                {/* Familiar "Collectoren" shortcut, tucked under Dashboard. Jumps
                    to the Dashboard and opens its collectoren section. */}
                {tab.name === 'dashboard' && showCollectorenLink && onCollectorenShortcut && (
                  <li>
                    <button
                      type="button"
                      aria-current={collectorenActive ? 'page' : undefined}
                      onClick={onCollectorenShortcut}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        collectorenActive
                          ? 'bg-td-blue text-white'
                          : 'text-td-navy hover:bg-gray-100'
                      }`}
                    >
                      <CollectorIcon />
                      <span>Collectoren</span>
                    </button>
                  </li>
                )}
              </Fragment>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
